import { db } from '../config/firebase';
import { 
  doc, 
  setDoc, 
  getDoc, 
  updateDoc, 
  arrayUnion, 
  arrayRemove,
  collection,
  query,
  where,
  getDocs,
  addDoc,
  orderBy,
  limit,
  serverTimestamp,
  deleteDoc,
  writeBatch,
  or,
  and,
  collectionGroup,
  Firestore,
  startAfter,
  runTransaction,
  documentId
} from 'firebase/firestore';
import { getShowDetails, getStreamingSourcesForShows, getStreamingSources } from './watchmodeApi';
import { cacheData, getCachedData } from '../utils/cache';
import { getIMDbRating as getOMDbRating } from './omdbApi';
import { getAuth } from 'firebase/auth';
import { getIMDbDetails } from './omdbApi';

const wait = (ms) => new Promise(resolve => setTimeout(resolve, ms));

const withRetry = async (fn, maxRetries = 3) => {
  for (let i = 0; i < maxRetries; i++) {
    try {
      return await fn();
    } catch (error) {
      if (i === maxRetries - 1) throw error;
      await wait(Math.pow(2, i) * 1000);
    }
  }
};

export const ensureAuthenticated = async () => {
  const auth = getAuth();
  if (auth.currentUser) return; // Return immediately if already authenticated
  
  // Only wait if we need to
  return new Promise((resolve) => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        unsubscribe(); // Clean up listener
        resolve();
      }
    });
  });
};

// User Management
export async function createUserProfile(user) {
  if (!user) {
    console.warn('⚠️ Attempted to create profile for null user');
    return;
  }
  
  console.group('👤 createUserProfile Operation');
  console.log('Starting profile creation/update for:', {
    uid: user.uid,
    displayName: user.displayName,
    hasEmail: !!user.email,
    hasPhoto: !!user.photoURL
  });
  
  const userRef = doc(db, 'users', user.uid);
  
  try {
    const userDoc = await getDoc(userRef);
    const userData = userDoc.data();
    
    if (userDoc.exists()) {
      console.log('📝 Existing user found, checking for updates');
      const needsUpdate = 
        userData.displayName !== user.displayName ||
        userData.email !== user.email ||
        userData.photoURL !== user.photoURL;
      
      if (!needsUpdate) {
        console.log('✅ User profile up to date, no changes needed');
        console.groupEnd();
        return userData;
      }
      console.log('🔄 Updates needed for user profile');
    } else {
      console.log('🆕 Creating new user profile');
    }

    const defaultAvatar = 'https://ui-avatars.com/api/?name=' + 
      encodeURIComponent(user.displayName || 'User') + 
      '&background=random&color=fff';

    const userProfile = {
      uid: user.uid,
      email: user.email,
      displayName: user.displayName || 'Anonymous User',
      photoURL: user.photoURL || defaultAvatar,
      lastUpdated: serverTimestamp()
    };

    console.log('💾 Saving user profile:', {
      ...userProfile,
      lastUpdated: 'serverTimestamp'
    });
    
    await setDoc(userRef, userProfile, { merge: true });
    invalidateUserCache(); // Invalidate cache when a user is updated
    
    console.log('✅ User profile saved successfully');
    console.groupEnd();
    return userProfile;
    
  } catch (error) {
    console.error('❌ Error in createUserProfile:', {
      error: error.message,
      stack: error.stack
    });
    console.groupEnd();
    throw error;
  }
}

// Watchlist Management
const removeFromAllLists = async (batch, userId, showId) => {
  const collections = ['watchlist', 'seen', 'watching', 'archived'];
  collections.forEach(collection => {
    const docRef = doc(db, 'users', userId, collection, showId.toString());
    batch.delete(docRef);
  });
};

const ensureShowExists = async (show) => {
  const showRef = doc(db, 'shows', show.id.toString());
  const showDoc = await getDoc(showRef);
  
  // Helper function to safely parse total_seasons
  const parseTotalSeasons = (seasons) => {
    if (!seasons) return null;
    const parsed = parseInt(seasons);
    return isNaN(parsed) ? null : parsed;
  };

  if (!showDoc.exists()) {
    await setDoc(showRef, {
      ...show,
      total_seasons: parseTotalSeasons(show.total_seasons),
      ratingSum: 0,
      ratingCount: 0,
      averageRating: 0,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
  } else {
    const existingData = showDoc.data();
    await setDoc(showRef, {
      ...existingData,
      ...show,
      total_seasons: parseTotalSeasons(show.total_seasons),
      updatedAt: serverTimestamp()
    }, { merge: true });
  }
};

// Helper function to clean show data before saving to Firestore
const cleanShowData = (show) => {
  const cleanedData = {
    id: show.id,
    title: show.title,
    type: show.type || 'movie',
    poster: show.poster || null,
    backdrop: show.backdrop || null,
    plot_overview: show.plot_overview || null,
    year: show.year || null,
    imdb_id: show.imdb_id || show.id,
    imdb_rating: show.imdb_rating || null,
    actors: show.actors || null,
    creators: show.creators || null,
    genre: show.genre || null,
    rated: show.rated || null,
    runtime: show.runtime || null,
    total_seasons: show.total_seasons || null,
    streamingSources: show.streamingSources || [],
    updatedAt: serverTimestamp()
  };

  // Remove any remaining undefined values
  Object.keys(cleanedData).forEach(key => 
    cleanedData[key] === undefined && delete cleanedData[key]
  );

  return cleanedData;
};

export const addToWatchlist = async (userId, show) => {
  try {
    const cleanedShow = cleanShowData(show);
    
    // First ensure the show exists in the shows collection
    const showRef = doc(db, 'shows', show.id.toString());
    await setDoc(showRef, cleanedShow, { merge: true });

    // Add to user's watchlist
    const watchlistRef = doc(db, 'users', userId, 'watchlist', show.id.toString());
    await setDoc(watchlistRef, {
      ...cleanedShow,
      addedAt: serverTimestamp(),
      status: 'watchlist'
    });

    // Remove from other collections if present
    const watchingRef = doc(db, 'users', userId, 'watching', show.id.toString());
    const seenRef = doc(db, 'users', userId, 'seen', show.id.toString());
    const archivedRef = doc(db, 'users', userId, 'archived', show.id.toString());

    await Promise.all([
      deleteDoc(watchingRef),
      deleteDoc(seenRef),
      deleteDoc(archivedRef)
    ]);

  } catch (error) {
    console.error('Error adding to watchlist:', error);
    throw error;
  }
};

export const removeFromWatchlist = async (userId, showId) => {
  const userRef = doc(db, 'users', userId);
  const userSnap = await getDoc(userRef);
  const watchlist = userSnap.data().watchlist;
  const showToRemove = watchlist.find(item => item.id === showId);
  
  await updateDoc(userRef, {
    watchlist: arrayRemove(showToRemove)
  });
};

// Add comment functions
export const addComment = async (userId, showId, text) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    const userName = userDoc.data().displayName || 'Anonymous';

    await addDoc(collection(db, 'shows', showId.toString(), 'comments'), {
      userId,
      userName,
      text,
      createdAt: new Date().toISOString()
    });
  } catch (error) {
    console.error('Error adding comment:', error);
    throw error;
  }
};

export const getShowComments = async (showId) => {
  try {
    const commentsSnap = await getDocs(
      query(
        collection(db, 'shows', showId.toString(), 'comments'),
        orderBy('createdAt', 'desc')
      )
    );
    
    return commentsSnap.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting comments:', error);
    return [];
  }
};

// Helper function to update show's aggregate rating data
const updateShowAggregateRatings = async (showId) => {
  try {
    // Get all ratings for the show directly from the show's ratings subcollection
    const ratingsSnapshot = await getDocs(
      collection(db, 'shows', showId.toString(), 'ratings')
    );

    const ratings = ratingsSnapshot.docs.map(doc => doc.data().rating);
    const ratingCount = ratings.length;
    const averageRating = ratingCount > 0 
      ? (ratings.reduce((sum, rating) => sum + rating, 0) / ratingCount).toFixed(1)
      : '0.0';

    // Update show document with new aggregate data
    const showRef = doc(db, 'shows', showId.toString());
    await setDoc(showRef, {
      averageRating,
      ratingCount,
      updatedAt: serverTimestamp()
    }, { merge: true });

    return { averageRating, ratingCount };
  } catch (error) {
    console.error('Error updating show aggregate ratings:', error);
    throw error;
  }
};

// Modified addRating function
export const addRating = async (userId, showId, rating) => {
  try {
    console.log('Adding rating in database service:', {
      userId,
      showId,
      rating
    });

    // Check if show exists first
    const showRef = doc(db, 'shows', showId.toString());
    const showDoc = await getDoc(showRef);
    
    if (!showDoc.exists()) {
      console.error('Show document not found:', {
        showId,
        showRef: showRef.path
      });
      throw new Error(`Show document ${showId} does not exist`);
    }

    // Use a transaction to handle concurrent rating updates
    const result = await runTransaction(db, async (transaction) => {
      const showRef = doc(db, 'shows', showId);
      const ratingRef = doc(db, 'shows', showId, 'ratings', userId);
      
      // Get the current user's rating (if it exists)
      const userRatingDoc = await transaction.get(ratingRef);
      const showDoc = await transaction.get(showRef);
      const showData = showDoc.data() || {};
      
      const oldRating = userRatingDoc.exists() ? Number(userRatingDoc.data().rating) : 0;
      const oldRatingSum = Number(showData.ratingSum || 0);
      const oldRatingCount = Number(showData.ratingCount || 0);

      // Calculate new values
      const numericRating = Number(rating);
      let newRatingSum;
      let newRatingCount;

      if (userRatingDoc.exists()) {
        // Update existing rating
        newRatingSum = oldRatingSum - oldRating + numericRating;
        newRatingCount = oldRatingCount; // Count stays the same
      } else {
        // Add new rating
        newRatingSum = oldRatingSum + numericRating;
        newRatingCount = oldRatingCount + 1;
      }

      const newAverageRating = Number((newRatingCount > 0 ? newRatingSum / newRatingCount : 0).toFixed(1));

      // Update the user's rating
      transaction.set(ratingRef, {
        userId,
        rating: numericRating,
        timestamp: new Date()
      });

      // Update show statistics
      transaction.update(showRef, {
        ratingSum: newRatingSum,
        ratingCount: newRatingCount,
        averageRating: newAverageRating
      });

      return {
        averageRating: newAverageRating,
        ratingCount: newRatingCount
      };
    });

    return result;
  } catch (error) {
    console.error('Error adding rating:', error);
    throw error;
  }
};

export const updateRating = async (userId, showId, rating, review = '') => {
  const ratingRef = doc(db, 'ratings', `${userId}_${showId}`);
  await updateDoc(ratingRef, {
    rating,
    review,
    updatedAt: new Date()
  });
};

// Add memoization for ratings
const ratingsCache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export const getShowRatings = async (showId) => {
  try {
    // Get the show document first for the pre-calculated values
    const showDoc = await getDoc(doc(db, 'shows', showId));
    const showData = showDoc.data() || {};
    
    // Get all individual ratings
    const ratingsRef = collection(db, 'shows', showId, 'ratings');
    const snapshot = await getDocs(ratingsRef);
    
    const ratings = snapshot.docs.map(doc => ({
      id: doc.id,
      userId: doc.data().userId,
      rating: doc.data().rating,
      timestamp: doc.data().timestamp,
      review: doc.data().review
    }));

    // Use the pre-calculated values from the show document
    return {
      ratings,
      averageRating: showData.averageRating || 0,
      ratingCount: showData.ratingCount || 0
    };
  } catch (error) {
    console.error('Error in getShowRatings:', error);
    return { ratings: [], averageRating: 0, ratingCount: 0 };
  }
};

// Add batch ratings fetch
export const getShowRatingsBatch = async (showIds) => {
  const promises = showIds.map(id => getShowRatings(id));
  return Promise.all(promises);
};

// Add function to get show's average rating
export const getShowAverageRating = async (showId) => {
  const cacheKey = `avgRating_${showId}`;
  const cachedData = getCachedData(cacheKey);
  if (cachedData) return cachedData;

  try {
    const showDoc = await getDoc(doc(db, 'shows', showId.toString()));
    if (!showDoc.exists()) return 0;
    return showDoc.data().averageRating || 0;
  } catch (error) {
    console.error('Error getting average rating:', error);
    return 0;
  }

  cacheData(cacheKey, averageRating, 5 * 60 * 1000); // Cache for 5 minutes
  return averageRating;
};

// Friend Management
export const sendFriendRequest = async (senderId, receiverId) => {
  try {
    // Create a unique ID for the friend request
    const requestId = `${senderId}_${receiverId}`;
    const requestRef = doc(db, 'friendRequests', requestId);

    await setDoc(requestRef, {
      senderId,
      receiverId,
      status: 'pending',
      createdAt: serverTimestamp()
    });

    console.log('Friend request created with ID:', requestId);
  } catch (error) {
    console.error('Error sending friend request:', error);
    throw error;
  }
};

export const acceptFriendRequest = async (currentUserId, senderId) => {
  try {
    // Create batch
    const batch = writeBatch(db);
    
    // Update both users' friends arrays
    const currentUserRef = doc(db, 'users', currentUserId);
    const senderRef = doc(db, 'users', senderId);

    batch.update(currentUserRef, {
      friends: arrayUnion(senderId)
    });

    batch.update(senderRef, {
      friends: arrayUnion(currentUserId)
    });

    // Delete the friend request - using the correct document ID format
    const requestRef = doc(db, 'friendRequests', `${senderId}_${currentUserId}`);
    batch.delete(requestRef);

    // Commit the batch
    await batch.commit();
    console.log('Friend request accepted and processed successfully');

  } catch (error) {
    console.error('Detailed error in acceptFriendRequest:', error);
    throw error;
  }
};

export const rejectFriendRequest = async (senderId, receiverId) => {
  try {
    const requestRef = doc(db, 'friendRequests', `${senderId}_${receiverId}`);
    await deleteDoc(requestRef);
  } catch (error) {
    console.error('Error rejecting friend request:', error);
    throw error;
  }
};

// Get friend's activity
export const getFriendsActivity = async (friends, lastTimestamp = null, pageSize = 10) => {
  try {
    if (!friends?.length) return [];

    // Build query with pagination
    let activityQuery = query(
      collection(db, 'activity'),
      where('userId', 'in', friends.slice(0, 10)), // Limit to max 10 friends at a time
      orderBy('timestamp', 'desc'),
      limit(pageSize)
    );

    // Add timestamp filter if paginating
    if (lastTimestamp) {
      activityQuery = query(
        activityQuery,
        where('timestamp', '<', lastTimestamp)
      );
    }

    const snapshot = await getDocs(activityQuery);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting friends activity:', error);
    return [];
  }
};

// Helper function to get activity for large friend groups
export const getAllFriendsActivity = async (friends, pageSize = 10) => {
  // Split friends into chunks of 10
  const friendChunks = [];
  for (let i = 0; i < friends.length; i += 10) {
    friendChunks.push(friends.slice(i, i + 10));
  }

  // Get activity for each chunk and merge results
  const activities = await Promise.all(
    friendChunks.map(chunk => getFriendsActivity(chunk, null, pageSize))
  );

  // Merge and sort all activities
  return activities
    .flat()
    .sort((a, b) => b.timestamp - a.timestamp)
    .slice(0, pageSize);
};

// Add this new function
export const getAllUsers = async () => {
  try {
    console.group('📊 getAllUsers Operation');
    
    // Check memory cache first
    const now = Date.now();
    const cachedData = getCachedData('allUsers');
    if (cachedData) {
      console.log('📦 Retrieved users from cache');
      console.groupEnd();
      return cachedData;
    }

    console.log('🔄 Cache miss - fetching users from database');
    
    const usersRef = collection(db, 'users');
    const snapshot = await getDocs(usersRef);
    
    const users = snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        ...data,
        uid: doc.id,
        photoURL: data.photoURL || null,
        displayName: data.displayName || 'Anonymous',
        createdAt: data.createdAt?.toDate() || new Date()
      };
    });

    // Cache the data
    cacheData('allUsers', users, CACHE_DURATION);
    
    console.log(`✨ Cached ${users.length} users`);
    console.groupEnd();
    return users;

  } catch (error) {
    console.error('Error getting all users:', error);
    throw error;
  }
};

// Add function to invalidate cache when a user profile is updated
export const invalidateUserCache = () => {
  // Clear the allUsers cache
  sessionStorage.removeItem('allUsers');
  // Could also clear individual user caches if needed
  // This would require keeping track of cached user IDs
};

// Make sure these functions are also exported
export const getFriends = async (userId) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (!userDoc.exists()) return [];
    
    const friendIds = userDoc.data().friends || [];
    const friendsData = await Promise.all(
      friendIds.map(async (friendId) => {
        const friendDoc = await getDoc(doc(db, 'users', friendId));
        return friendDoc.exists() ? { uid: friendDoc.id, ...friendDoc.data() } : null;
      })
    );
    
    return friendsData.filter(friend => friend !== null);
  } catch (error) {
    console.error('Error getting friends:', error);
    return [];
  }
};

export const addFriend = async (userId, friendId) => {
  try {
    await setDoc(doc(db, 'users', userId), {
      friends: arrayUnion(friendId)
    }, { merge: true });
  } catch (error) {
    console.error('Error adding friend:', error);
    throw error;
  }
};

// Add these new functions
export const getUserProfile = async (userId) => {
  try {
    const cacheKey = `user_${userId}`;
    const cachedData = getCachedData(cacheKey);
    
    if (cachedData) {
      return cachedData;
    }

    const userDoc = await getDoc(doc(db, 'users', userId));
    if (!userDoc.exists()) {
      return null;
    }

    const userData = {
      uid: userDoc.id,
      ...userDoc.data()
    };

    // Cache the user data
    cacheData(cacheKey, userData, CACHE_DURATION);

    return userData;
  } catch (error) {
    console.error('Error getting user profile:', error);
    throw error;
  }
};

export const getFriendRatings = async (userId) => {
  try {
    // Query all ratings across all shows where userId matches
    const ratingsQuery = query(
      collectionGroup(db, 'ratings'),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    );
    
    const snapshot = await getDocs(ratingsQuery);
    console.log(`Found ${snapshot.size} ratings for user ${userId}`);
    
    // Get all show IDs from the ratings
    const showIds = snapshot.docs.map(doc => doc.ref.parent.parent.id);
    
    // Fetch show documents to get average ratings
    const showsRef = collection(db, 'shows');
    const showsData = {};
    
    // Process showIds in batches of 30 (Firestore limit)
    for (let i = 0; i < showIds.length; i += 30) {
      const batch = showIds.slice(i, i + 30);
      const q = query(showsRef, where(documentId(), 'in', batch));
      const showsSnapshot = await getDocs(q);
      
      showsSnapshot.forEach(doc => {
        showsData[doc.id] = {
          averageRating: doc.data().averageRating || 0,
          ratingCount: doc.data().ratingCount || 0
        };
      });
    }
    
    return snapshot.docs.map(doc => {
      const data = doc.data();
      const showId = doc.ref.parent.parent.id;
      const showInfo = showsData[showId] || { averageRating: 0, ratingCount: 0 };
      
      return {
        showId,
        rating: data.rating, // Personal rating
        averageRating: showInfo.averageRating, // Show's average rating
        ratingCount: showInfo.ratingCount,
        createdAt: data.createdAt || data.timestamp,
        review: data.review || '',
        userId: data.userId
      };
    });
  } catch (error) {
    console.error('Error getting friend ratings:', error);
    return [];
  }
};

// Add this function with your other exports
export const getUserRating = async (userId, showId) => {
  try {
    console.log('Getting rating for user:', userId, 'show:', showId);
    const ratingRef = doc(db, 'users', userId, 'ratings', showId.toString());
    const ratingSnap = await getDoc(ratingRef);
    console.log('Rating exists:', ratingSnap.exists(), 'Data:', ratingSnap.data());
    if (!ratingSnap.exists()) return null;
    return ratingSnap.data();
  } catch (error) {
    console.error('Error getting user rating:', error);
    return null;
  }
};

// Add these functions to handle average ratings
export const updateShowAverageRating = async (showId) => {
  try {
    const ratingsQuery = query(collection(db, 'ratings'), 
      where('showId', '==', showId.toString())
    );
    const ratingsSnap = await getDocs(ratingsQuery);
    
    let totalRating = 0;
    let count = 0;
    
    ratingsSnap.forEach(doc => {
      totalRating += doc.data().rating;
      count++;
    });
    
    const averageRating = count > 0 ? totalRating / count : 0;
    
    await setDoc(doc(db, 'shows', showId.toString()), {
      averageRating,
      ratingCount: count
    }, { merge: true });
    
    return averageRating;
  } catch (error) {
    console.error('Error updating average rating:', error);
    throw error;
  }
};

// Add these to your existing databaseService.js
export const addSearchHistory = async (userId, searchTerm) => {
  if (!userId || !searchTerm) return;

  const searchesRef = collection(db, 'users', userId, 'searches');
  
  try {
    await addDoc(searchesRef, {
      term: searchTerm,
      timestamp: serverTimestamp()
    });
  } catch (error) {
    console.error('Error adding search history:', error);
    throw error;
  }
};

export const getSearchHistory = async (userId) => {
  if (!userId) return [];

  try {
    const searchesRef = collection(db, 'users', userId, 'searches');
    const q = query(searchesRef, 
      orderBy('timestamp', 'desc'), 
      limit(10)  // Only get last 10 searches
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      timestamp: doc.data().timestamp?.toDate()
    }));
  } catch (error) {
    console.error('Error getting search history:', error);
    return [];
  }
};

export const deleteSearchHistory = async (userId, searchId) => {
  if (!userId || !searchId) return;

  try {
    const searchRef = doc(db, 'users', userId, 'searches', searchId);
    await deleteDoc(searchRef);
  } catch (error) {
    console.error('Error deleting search history:', error);
    throw error;
  }
};

export const getShowFromUserLists = async (userId, showId) => {
  try {
    const [watchlistDoc, seenDoc, archivedDoc, watchingDoc] = await Promise.all([
      getDoc(doc(db, 'users', userId, 'watchlist', showId.toString())),
      getDoc(doc(db, 'users', userId, 'seen', showId.toString())),
      getDoc(doc(db, 'users', userId, 'archived', showId.toString())),
      getDoc(doc(db, 'users', userId, 'watching', showId.toString()))
    ]);

    return {
      inWatchlist: watchlistDoc.exists(),
      inSeenList: seenDoc.exists(),
      inArchived: archivedDoc.exists(),
      inWatching: watchingDoc.exists(),
      id: showId,
      addedAt: watchingDoc.exists() ? watchingDoc.data().addedAt :
               watchlistDoc.exists() ? watchlistDoc.data().addedAt : 
               seenDoc.exists() ? seenDoc.data().addedAt :
               archivedDoc.exists() ? archivedDoc.data().addedAt : null
    };
  } catch (error) {
    console.error('Error checking show in lists:', error);
    return {
      inWatchlist: false,
      inSeenList: false,
      inArchived: false,
      inWatching: false,
      id: showId,
      addedAt: null
    };
  }
};

export const getPendingRequests = async (userId) => {
  try {
    const q = query(
      collection(db, 'friendRequests'),
      and(
        where('status', '==', 'pending'),
        or(
          where('senderId', '==', userId),
          where('receiverId', '==', userId)
        )
      )
    );
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting pending requests:', error);
    throw error;
  }
};

export const removeFriend = async (userId, friendId) => {
  try {
    const userRef = doc(db, 'users', userId);
    const friendRef = doc(db, 'users', friendId);

    // Remove friend from both users' friends arrays
    await Promise.all([
      updateDoc(userRef, {
        friends: arrayRemove(friendId)
      }),
      updateDoc(friendRef, {
        friends: arrayRemove(userId)
      })
    ]);
  } catch (error) {
    console.error('Error removing friend:', error);
    throw error;
  }
};

export const cancelFriendRequest = async (senderId, receiverId) => {
  try {
    const requestRef = doc(db, 'friendRequests', `${senderId}_${receiverId}`);
    await deleteDoc(requestRef);
  } catch (error) {
    console.error('Error canceling friend request:', error);
    throw error;
  }
};

// Add these new functions
export const addTagToShow = async (userId, showId, tag) => {
  try {
    const showRef = doc(db, 'users', userId, 'tags', showId.toString());
    await setDoc(showRef, {
      tags: arrayUnion(tag)
    }, { merge: true });
  } catch (error) {
    console.error('Error adding tag:', error);
    throw error;
  }
};

export const removeTagFromShow = async (userId, showId, tag) => {
  try {
    const showRef = doc(db, 'users', userId, 'tags', showId.toString());
    await updateDoc(showRef, {
      tags: arrayRemove(tag)
    });
  } catch (error) {
    console.error('Error removing tag:', error);
    throw error;
  }
};

export const getShowTags = async (userId, showId) => {
  try {
    const showRef = doc(db, 'users', userId, 'tags', showId.toString());
    const showDoc = await getDoc(showRef);
    return showDoc.exists() ? showDoc.data().tags : [];
  } catch (error) {
    console.error('Error getting tags:', error);
    return [];
  }
};

export const updateMissingIMDbRatings = async (showId) => {
  try {
    const showDoc = await getDoc(doc(db, 'shows', showId.toString()));
    const showData = showDoc.data();

    if (showData && showData.imdb_id && !showData.imdb_rating) {
      const imdbRating = await getOMDbRating(showData.imdb_id);
      console.log('Updating missing IMDb rating for', showData.title, ':', imdbRating);

      if (imdbRating) {
        await setDoc(doc(db, 'shows', showId.toString()), {
          ...showData,
          imdb_rating: imdbRating,
          updatedAt: serverTimestamp()
        });
      }
    }
  } catch (error) {
    console.error('Error updating missing IMDb rating:', error);
  }
};

export const updateMissingBackdrops = async (showId) => {
  try {
    const showDoc = await getDoc(doc(db, 'shows', showId.toString()));
    const showData = showDoc.data();

    if (showData && !showData.backdrop) {
      const showDetails = await getShowDetails(showId);
      console.log('Updating missing backdrop for', showData.title);

      if (showDetails.backdrop) {
        await setDoc(doc(db, 'shows', showId.toString()), {
          ...showData,
          backdrop: showDetails.backdrop,
          updatedAt: serverTimestamp()
        });
      }
    }
  } catch (error) {
    console.error('Error updating missing backdrop:', error);
  }
};

// Add function to clean up ratings when user is deleted
export const cleanupUserRatings = async (userId) => {
  try {
      const showsWithRatings = await getDocs(
        query(collectionGroup(db, 'ratings'),
        where('userId', '==', userId))
      );
      
      const batch = writeBatch(db);
      const batchPromises = []; // Store promises for batch operations

      showsWithRatings.forEach((ratingDoc) => {
          const showId = ratingDoc.ref.parent.parent.id;
          const showRef = doc(db, 'shows', showId);
          const ratingRef = doc(db, 'shows', showId, 'ratings', userId);

          batchPromises.push(getDoc(showRef).then(async (showDoc) => {
              const showData = showDoc.data();
              const rating = ratingDoc.data().rating;
      
              // Delete the rating
              batch.delete(ratingRef);
      
              // Update show statistics
              batch.update(showRef, {
                  ratingCount: (showData.ratingCount || 0) - 1,
                  ratingSum: (showData.ratingSum || 0) - rating,
                  averageRating: ((showData.ratingSum || 0) - rating) / ((showData.ratingCount || 0) - 1)
              });
            }));
      });

      await Promise.all(batchPromises);
      await batch.commit();

  } catch (error) {
      console.error('Error cleaning up user ratings:', error);
  }
};

const highestRatedShowsCache = new Map();

export const getHighestRatedShows = async (limitCount = 20, type = null, lastDoc = null) => {
  try {
    let baseQuery = query(
      collection(db, 'shows'),
      orderBy('averageRating', 'desc'),
      orderBy('ratingCount', 'desc')
    );

    if (type) {
      baseQuery = query(baseQuery, where('type', '==', type));
    }

    // Add limit and startAfter if we have a last document
    let finalQuery = query(baseQuery, limit(limitCount));
    if (lastDoc) {
      finalQuery = query(finalQuery, startAfter(lastDoc));
    }

    const snapshot = await getDocs(finalQuery);
    const shows = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Return both the shows and the last document
    return { 
      shows,
      lastDoc: snapshot.docs[snapshot.docs.length - 1] || null
    };
  } catch (error) {
    console.error('Error getting highest rated shows:', error);
    return { shows: [], lastDoc: null };
  }
};

export const searchShows = async (query, page = 1, limit = 30, lastDoc = null) => {
  try {
    const showsRef = collection(db, 'shows');
    
    // Build base query
    let baseQuery = query(
      showsRef,
      where('title', '>=', query.toUpperCase()),
      where('title', '<=', query.toUpperCase() + '\uf8ff'),
      orderBy('title'),
      limit(limit)
    );

    // Add startAfter if we have a last document
    if (lastDoc) {
      baseQuery = query(baseQuery, startAfter(lastDoc));
    }

    const snapshot = await getDocs(baseQuery);
    const lastVisible = snapshot.docs[snapshot.docs.length - 1];
    
    // Get more complete data in the initial query
    const shows = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      plot: doc.data().plot || '',
      imdb_rating: doc.data().imdb_rating || '',
      total_seasons: doc.data().total_seasons || null,
      genre: doc.data().genre || '',
      actors: doc.data().actors || ''
    }));

    return {
      shows,
      hasMore: snapshot.docs.length === limit,
      currentPage: page,
      lastDoc: lastVisible
    };
  } catch (error) {
    console.error('Error searching shows:', error);
    return {
      shows: [],
      hasMore: false,
      currentPage: page,
      lastDoc: null
    };
  }
};

// Add these new functions for Tips
export const getTipsList = async (sourceId, page = 1, lastDoc = null, limit = 10) => {
  try {
    console.log('Starting query with filters...');
    console.log('Source ID:', sourceId);

    // Create base query with where clause
    const q = query(
      collection(db, 'TipsLists'),
      where('source_id', '==', 'DN')
    );
    console.log('Query with filters created');

    // Execute query
    const querySnapshot = await getDocs(q);
    console.log('Query executed, documents found:', querySnapshot.size);

    if (querySnapshot.empty) {
      console.log('No documents found');
      return {
        tipsList: [],
        hasMore: false,
        lastDoc: null
      };
    }

    // Map documents to data
    const tipsData = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    console.log('Processed tips data:', tipsData);

    return {
      tipsList: tipsData,
      hasMore: false,
      lastDoc: null
    };

  } catch (error) {
    console.error('Detailed error:', error);
    throw error;
  }
};

export const migrateRatingsToNumbers = async () => {
  try {
    const showsRef = collection(db, 'shows');
    const snapshot = await getDocs(showsRef);
    const batch = writeBatch(db);
    let batchCount = 0;
    const batchLimit = 500;

    for (const doc of snapshot.docs) {
      const data = doc.data();
      if (data.averageRating && typeof data.averageRating === 'string') {
        batch.update(doc.ref, {
          averageRating: Number(data.averageRating),
          ratingCount: Number(data.ratingCount || 0),
          ratingSum: Number(data.ratingSum || 0)
        });
        batchCount++;

        if (batchCount >= batchLimit) {
          await batch.commit();
          batchCount = 0;
        }
      }
    }

    if (batchCount > 0) {
      await batch.commit();
    }

    console.log('Rating migration completed successfully');
  } catch (error) {
    console.error('Error migrating ratings:', error);
    throw error;
  }
};

export const fixNullRatings = async () => {
  try {
    const showsRef = collection(db, 'shows');
    const snapshot = await getDocs(showsRef);
    const batch = writeBatch(db);
    let batchCount = 0;
    const batchLimit = 500;
    let fixedCount = 0;

    for (const doc of snapshot.docs) {
      const data = doc.data();
      const rating = data.averageRating;
      
      // Check for null, undefined, NaN, or non-numeric values
      if (rating === null || rating === undefined || isNaN(rating) || typeof rating !== 'number') {
        batch.update(doc.ref, {
          averageRating: 0,
          ratingCount: Number(data.ratingCount || 0),
          ratingSum: Number(data.ratingSum || 0)
        });
        
        batchCount++;
        fixedCount++;

        if (batchCount >= batchLimit) {
          await batch.commit();
          batchCount = 0;
        }
      }
    }

    if (batchCount > 0) {
      await batch.commit();
    }

    console.log(`Fixed ${fixedCount} shows with null/NaN ratings`);
    return fixedCount;
  } catch (error) {
    console.error('Error fixing null ratings:', error);
    throw error;
  }
};

export const getUserSeenList = async (userId) => {
  try {
    const seenRef = collection(db, `users/${userId}/seen`);
    const snapshot = await getDocs(seenRef);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting user seen list:', error);
    return [];
  }
};

// Add these new functions for archive functionality
export const getArchivedShows = async (userId) => {
  try {
    const archivedRef = collection(db, 'users', userId, 'archived');
    const snapshot = await getDocs(archivedRef);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting archived shows:', error);
    return [];
  }
};

// Update the existing getShowStatus function to include archived status
export const getShowStatus = (show, watchlist, seenList, archivedList, watchingList) => {
  const isInArchived = archivedList?.some(item => String(item.id) === String(show.id));
  const isInSeen = seenList?.some(item => String(item.id) === String(show.id));
  const isInWatchlist = watchlist?.some(item => String(item.id) === String(show.id));
  const isWatching = watchingList?.some(item => String(item.id) === String(show.id));
  
  if (isInArchived) return 'archived';
  if (isInSeen) return 'seen';
  if (isWatching) return 'watching';
  if (isInWatchlist) return 'watchlist';
  return 'none';
};

export const getSeenShows = async (userId) => {
  try {
    const seenRef = collection(db, 'users', userId, 'seen');
    const snapshot = await getDocs(seenRef);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting seen shows:', error);
    return [];
  }
};

export const markShowAsSeen = async (userId, show) => {
  try {
    await ensureShowExists(show);
    const batch = writeBatch(db);
    
    // Remove from other lists
    await removeFromAllLists(batch, userId, show.id);
    
    // Add to seen list - only store essential show data
    const seenRef = doc(db, 'users', userId, 'seen', show.id.toString());
    const { ratingSum, ratingCount, averageRating, ...essentialShowData } = show;
    batch.set(seenRef, {
      ...essentialShowData,
      addedAt: serverTimestamp()
    });
    
    await batch.commit();
  } catch (error) {
    console.error('Error marking show as seen:', error);
    throw error;
  }
};

export const archiveShow = async (userId, show) => {
  try {
    const batch = writeBatch(db);
    
    // Remove from all other collections
    await removeFromAllLists(batch, userId, show.id);
    
    // Add to archived collection
    const archivedRef = doc(db, 'users', userId, 'archived', show.id.toString());
    batch.set(archivedRef, {
      ...show,
      addedAt: serverTimestamp()
    });
    
    await batch.commit();
  } catch (error) {
    console.error('Error archiving show:', error);
    throw error;
  }
};

export const unarchiveShow = async (userId, showId) => {
  try {
    const archivedRef = doc(db, 'users', userId, 'archived', showId.toString());
    await deleteDoc(archivedRef);
    return true;
  } catch (error) {
    console.error('Error unarchiving show:', error);
    throw error;
  }
};

// Add this function to handle batch loading of user lists
export const getUserLists = async (userId) => {
  try {
    // Get all lists in parallel
    const [watchlistSnapshot, seenSnapshot, watchingSnapshot, archivedSnapshot] = await Promise.all([
      getDocs(collection(db, 'users', userId, 'watchlist')),
      getDocs(collection(db, 'users', userId, 'seen')),
      getDocs(collection(db, 'users', userId, 'watching')),
      getDocs(collection(db, 'users', userId, 'archived'))
    ]);

    return {
      watchlist: watchlistSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })),
      seenList: seenSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })),
      watchingList: watchingSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })),
      archivedList: archivedSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
    };
  } catch (error) {
    console.error('Error getting user lists:', error);
    return {
      watchlist: [],
      seenList: [],
      watchingList: [],
      archivedList: []
    };
  }
};

const fetchAndStoreShowData = async (id, imdbId) => {
  try {
    console.log('Fetching data for show:', { id, imdbId });

    // Fetch data from both APIs in parallel
    const [imdbData, watchmodeData, streamingSources] = await Promise.all([
      imdbId ? getIMDbDetails(imdbId) : null,
      getShowDetails(id),
      getStreamingSources(id)
    ]);

    // Debug logs to trace type information
    console.log('Raw API responses:', {
      imdbType: imdbData?.Type,
      watchmodeType: watchmodeData?.type
    });

    // Determine type with explicit logic
    const showType = imdbData?.Type?.toLowerCase() === 'movie' || 
                    watchmodeData?.type?.toLowerCase() === 'movie' ||
                    imdbData?.Type?.toLowerCase() === 'film' ||
                    watchmodeData?.type?.toLowerCase() === 'film'
                    ? 'movie' 
                    : 'tv_series';

    console.log('Determined show type:', showType);

    // Helper function to safely parse total_seasons
    const parseTotalSeasons = (seasons) => {
      if (!seasons) return null;
      const parsed = parseInt(seasons);
      return isNaN(parsed) ? null : parsed;
    };

    // Combine data, prioritizing OMDB data
    const showData = {
      id: id,
      title: imdbData?.Title || watchmodeData.title || '',
      year: imdbData?.Year || watchmodeData.year || null,
      poster: (imdbData?.Poster && imdbData.Poster !== 'N/A') ? imdbData.Poster : 
             (watchmodeData.poster || watchmodeData.poster_url || null),
      backdrop: watchmodeData.backdrop || null,
      plot_overview: imdbData?.Plot || watchmodeData.plot_overview || '',
      imdb_id: imdbId || watchmodeData.imdb_id || null,
      imdb_rating: imdbData?.imdbRating || null,
      total_seasons: parseTotalSeasons(imdbData?.totalSeasons),
      actors: imdbData?.Actors || watchmodeData.cast_summary || '',
      creators: imdbData?.Writer || watchmodeData.creators_summary || '',
      genre: imdbData?.Genre || '',
      runtime: imdbData?.Runtime || '',
      rated: imdbData?.Rated || '',
      streamingSources: Array.isArray(streamingSources) ? streamingSources : [],
      type: showType,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    console.log('Final show data being stored:', showData);

    // Store in Firestore
    await setDoc(doc(db, 'shows', id.toString()), showData);

    return showData;
  } catch (error) {
    console.error('Error fetching and storing show data:', error);
    throw error;
  }
};

// Add this new function to handle migration of empty photoURLs
export const migrateEmptyPhotoURLs = async () => {
  try {
    const usersRef = collection(db, 'users');
    const snapshot = await getDocs(
      query(usersRef, where('photoURL', '==', ''))
    );

    const batch = writeBatch(db);
    let batchCount = 0;
    const batchLimit = 500;
    let updatedCount = 0;

    for (const doc of snapshot.docs) {
      const userData = doc.data();
      const defaultAvatar = 'https://ui-avatars.com/api/?name=' + 
        encodeURIComponent(userData.displayName || 'User') + 
        '&background=random&color=fff';

      batch.update(doc.ref, {
        photoURL: defaultAvatar,
        lastUpdated: serverTimestamp()
      });

      batchCount++;
      updatedCount++;

      if (batchCount >= batchLimit) {
        await batch.commit();
        batchCount = 0;
      }
    }

    if (batchCount > 0) {
      await batch.commit();
    }

    console.log(`Updated ${updatedCount} users with empty photoURLs`);
    return updatedCount;
  } catch (error) {
    console.error('Error migrating empty photoURLs:', error);
    throw error;
  }
};

// Add new function to get watching shows
export const getWatchingShows = async (userId) => {
  try {
    const watchingRef = collection(db, 'users', userId, 'watching');
    const snapshot = await getDocs(watchingRef);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting watching shows:', error);
    return [];
  }
};

export const markShowAsWatching = async (userId, show) => {
  try {
    const cleanedShow = cleanShowData(show);
    
    // First ensure the show exists in the shows collection
    const showRef = doc(db, 'shows', show.id.toString());
    await setDoc(showRef, cleanedShow, { merge: true });

    // Add to user's watching collection
    const watchingRef = doc(db, 'users', userId, 'watching', show.id.toString());
    await setDoc(watchingRef, {
      ...cleanedShow,
      addedAt: serverTimestamp(),
      status: 'watching'
    });

    // Remove from other collections if present
    const watchlistRef = doc(db, 'users', userId, 'watchlist', show.id.toString());
    const seenRef = doc(db, 'users', userId, 'seen', show.id.toString());
    const archivedRef = doc(db, 'users', userId, 'archived', show.id.toString());

    await Promise.all([
      deleteDoc(watchlistRef),
      deleteDoc(seenRef),
      deleteDoc(archivedRef)
    ]);

  } catch (error) {
    console.error('Error marking show as watching:', error);
    throw error;
  }
};

export const getWatchlistShows = async (userId) => {
  try {
    const watchlistRef = collection(db, 'users', userId, 'watchlist');
    const snapshot = await getDocs(watchlistRef);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting watchlist shows:', error);
    return [];
  }
};

// Add this new function to check user's last activity
export const getUserLastActivity = async (userId) => {
  try {
    // Get the most recent activity from each relevant collection
    const [ratingsQuery, watchlistQuery, seenQuery, watchingQuery, archivedQuery] = await Promise.all([
      getDocs(query(
        collection(db, 'ratings'),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc'),
        limit(1)
      )),
      getDocs(query(
        collection(db, `users/${userId}/watchlist`),
        orderBy('addedAt', 'desc'),
        limit(1)
      )),
      getDocs(query(
        collection(db, `users/${userId}/seen`),
        orderBy('addedAt', 'desc'),
        limit(1)
      )),
      getDocs(query(
        collection(db, `users/${userId}/watching`),
        orderBy('addedAt', 'desc'),
        limit(1)
      )),
      getDocs(query(
        collection(db, `users/${userId}/archived`),
        orderBy('addedAt', 'desc'),
        limit(1)
      ))
    ]);

    // Get timestamps from each collection
    const timestamps = [];
    
    if (!ratingsQuery.empty) {
      timestamps.push(ratingsQuery.docs[0].data().createdAt?.toDate());
    }
    if (!watchlistQuery.empty) {
      timestamps.push(watchlistQuery.docs[0].data().addedAt?.toDate());
    }
    if (!seenQuery.empty) {
      timestamps.push(seenQuery.docs[0].data().addedAt?.toDate());
    }
    if (!watchingQuery.empty) {
      timestamps.push(watchingQuery.docs[0].data().addedAt?.toDate());
    }
    if (!archivedQuery.empty) {
      timestamps.push(archivedQuery.docs[0].data().addedAt?.toDate());
    }

    // Filter out any null/undefined timestamps
    const validTimestamps = timestamps.filter(t => t);

    if (validTimestamps.length === 0) {
      return null; // No activity found
    }

    // Return the most recent timestamp
    return new Date(Math.max(...validTimestamps.map(t => t.getTime())));

  } catch (error) {
    console.error('Error getting user last activity:', error);
    return null;
  }
};

// Add these new functions to track activities
export const addActivity = async (userId, action, showId, showTitle, value) => {
  try {
    const activityData = {
      userId,
      action,
      showId,
      showTitle,
      value,
      timestamp: serverTimestamp(),
      createdAt: serverTimestamp()
    };

    await addDoc(collection(db, 'activities'), activityData);
    console.log('Activity added successfully');
  } catch (error) {
    console.error('Error in addActivity:', error);
    throw error;
  }
};

export const getLatestActivities = async () => {
  try {
    const activitiesRef = collection(db, 'activities');
    const q = query(
      activitiesRef,
      orderBy('timestamp', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    
    // Group activities by userId
    const activitiesByUser = {};
    querySnapshot.forEach((doc) => {
      const activity = { id: doc.id, ...doc.data() };
      if (!activitiesByUser[activity.userId]) {
        activitiesByUser[activity.userId] = [];
      }
      activitiesByUser[activity.userId].push(activity);
    });

    console.log('Debug - Activities fetched:', activitiesByUser);
    return activitiesByUser;
  } catch (error) {
    console.error('Error getting latest activities:', error);
    return {};
  }
};

// Add function to track when a user last viewed another user's profile
export const updateLastViewedTimestamp = async (viewerId, viewedUserId) => {
  try {
    const viewerRef = doc(db, 'users', viewerId);
    await updateDoc(viewerRef, {
      [`lastViewed.${viewedUserId}`]: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating last viewed timestamp:', error);
  }
};

// Get user's last viewed timestamps
export const getLastViewedTimestamps = async (userId) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    return userDoc.data()?.lastViewed || {};
  } catch (error) {
    console.error('Error getting last viewed timestamps:', error);
    return {};
  }
};

// Add a function to fix existing NaN total_seasons in the database
export const fixNaNTotalSeasons = async () => {
  try {
    const showsRef = collection(db, 'shows');
    const snapshot = await getDocs(showsRef);
    const batch = writeBatch(db);
    let batchCount = 0;
    const batchLimit = 500;
    let fixedCount = 0;

    for (const doc of snapshot.docs) {
      const data = doc.data();
      const totalSeasons = data.total_seasons;
      
      if (totalSeasons !== null && (isNaN(totalSeasons) || typeof totalSeasons !== 'number')) {
        batch.update(doc.ref, {
          total_seasons: null,
          updatedAt: serverTimestamp()
        });
        
        batchCount++;
        fixedCount++;

        if (batchCount >= batchLimit) {
          await batch.commit();
          batchCount = 0;
        }
      }
    }

    if (batchCount > 0) {
      await batch.commit();
    }

    console.log(`Fixed ${fixedCount} shows with NaN total_seasons`);
    return fixedCount;
  } catch (error) {
    console.error('Error fixing NaN total_seasons:', error);
    throw error;
  }
};

export const fixRatingInconsistencies = async () => {
  try {
    console.log('Starting rating inconsistency fix...');
    const showsRef = collection(db, 'shows');
    const snapshot = await getDocs(showsRef);
    console.log(`Found ${snapshot.size} shows to process`);

    const batch = writeBatch(db);
    let batchCount = 0;
    const batchLimit = 500;
    let fixedCount = 0;
    let processedCount = 0;

    for (const showDoc of snapshot.docs) {
      processedCount++;
      if (processedCount % 100 === 0) {
        console.log(`Processed ${processedCount}/${snapshot.size} shows...`);
      }

      // Get all ratings from the subcollection
      const ratingsSnapshot = await getDocs(
        collection(db, 'shows', showDoc.id, 'ratings')
      );
      
      const ratings = ratingsSnapshot.docs.map(doc => Number(doc.data().rating));
      const ratingCount = ratings.length;
      const ratingSum = ratings.reduce((sum, rating) => sum + rating, 0);
      const averageRating = ratingCount > 0 
        ? Number((ratingSum / ratingCount).toFixed(1))
        : 0;

      const showData = showDoc.data();
      
      // Check if current values match calculated values
      if (
        showData.ratingCount !== ratingCount ||
        showData.ratingSum !== ratingSum ||
        showData.averageRating !== averageRating
      ) {
        console.log(`Fixing show ${showDoc.id}:`, {
          old: {
            ratingCount: showData.ratingCount,
            ratingSum: showData.ratingSum,
            averageRating: showData.averageRating
          },
          new: {
            ratingCount,
            ratingSum,
            averageRating
          }
        });

        batch.update(showDoc.ref, {
          ratingCount,
          ratingSum,
          averageRating,
          updatedAt: serverTimestamp()
        });
        
        fixedCount++;
        batchCount++;
        
        if (batchCount >= batchLimit) {
          console.log(`Committing batch of ${batchCount} updates...`);
          await batch.commit();
          batch = writeBatch(db); // Create new batch
          batchCount = 0;
          console.log('Batch committed successfully');
        }
      }
    }

    if (batchCount > 0) {
      console.log(`Committing final batch of ${batchCount} updates...`);
      await batch.commit();
      console.log('Final batch committed successfully');
    }

    console.log(`Fixed ${fixedCount} shows with inconsistent ratings`);
    return fixedCount;
  } catch (error) {
    console.error('Error fixing rating inconsistencies:', error);
    throw error;
  }
};